<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-2 mb-4">
      <div>
        <h6 class="ma-0 mb-1">Categorias</h6>
        <div class="text-caption text--secondary">Plano de contas</div>
      </div>
      <v-btn color="primary" depressed small @click="openCreate">
        Nova categoria
      </v-btn>
    </div>

    <div class="d-flex align-center flex-wrap gap-2 mb-3">
      <v-btn icon small :loading="loading" @click="loadCategories">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text class="mb-3">
      {{ error }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="categories"
      :loading="loading"
      dense
      class="elevation-0"
      no-data-text="Nenhuma categoria"
    >
      <template v-slot:item.name="{ item }">
        {{ item.name }}
        <v-chip v-if="item.code" x-small label outlined class="ml-1">
          Sistema
        </v-chip>
      </template>
      <template v-slot:item.dreLine="{ item }">
        {{ dreLineLabel(item.dreLine) }}
      </template>
      <template v-slot:item.active="{ item }">
        <v-chip :color="item.active ? 'success' : 'grey'" x-small label>
          {{ item.active ? "Ativa" : "Inativa" }}
        </v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click="openEdit(item)">mdi-pencil</v-icon>
        <v-icon
          v-if="canDelete(item)"
          small
          color="error"
          @click="remove(item)"
        >
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="480" :persistent="saving">
      <v-card>
        <v-card-title>
          {{ form.id ? "Editar categoria" : "Nova categoria" }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="formError" type="error" dense text class="mb-3">
            {{ formError }}
          </v-alert>
          <v-text-field
            v-model="form.name"
            label="Nome"
            outlined
            dense
            :disabled="saving"
          />
          <v-select
            v-model="form.dreLine"
            :items="dreLineItems"
            label="Linha do DRE"
            outlined
            dense
            :disabled="saving"
          />
          <v-switch
            v-model="form.active"
            label="Ativa"
            dense
            hide-details
            :disabled="saving || !!form.code"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="save">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CASH from "@/services/ultimate/cash.js";
import CASH_DEFINITIONS from "@/definitions/CashDefinitions";

export default {
  name: "CashCategories",
  props: {
    companyId: {
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      formError: null,
      dialog: false,
      categories: [],
      form: this.emptyForm(),
      headers: [
        { text: "Nome", value: "name" },
        { text: "Linha do DRE", value: "dreLine" },
        { text: "Lançamentos", value: "entryCount" },
        { text: "Situação", value: "active" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      dreLineItems: CASH_DEFINITIONS.dreLines,
    };
  },
  watch: {
    companyId() {
      this.loadCategories();
    },
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: "",
        dreLine: null,
        active: true,
        code: null,
        entryCount: 0,
      };
    },
    dreLineLabel(value) {
      const line = CASH_DEFINITIONS.dreLines.find((item) => item.value === value);
      return line ? line.text : value;
    },
    canDelete(item) {
      return !item.code && !item.entryCount;
    },
    openCreate() {
      this.form = this.emptyForm();
      this.formError = null;
      this.dialog = true;
    },
    openEdit(item) {
      this.form = {
        id: item.id,
        name: item.name,
        dreLine: item.dreLine,
        active: item.active,
        code: item.code,
        entryCount: item.entryCount || 0,
      };
      this.formError = null;
      this.dialog = true;
    },
    payload() {
      const data = {
        name: this.form.name,
        dreLine: this.form.dreLine,
        active: this.form.active,
      };
      if (!this.form.id) data.companyId = this.companyId || null;
      return data;
    },
    async save() {
      if (!this.form.name || !this.form.name.trim()) {
        this.formError = "Nome é obrigatório";
        return;
      }
      if (!this.form.dreLine) {
        this.formError = "Escolha a linha do DRE";
        return;
      }
      try {
        this.saving = true;
        this.formError = null;
        if (this.form.id) {
          await CASH.updateCategory(this.form.id, this.payload());
        } else {
          await CASH.createCategory(this.payload());
        }
        this.dialog = false;
        await this.loadCategories();
      } catch (error) {
        this.formError = error.message || "Erro ao salvar categoria";
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      if (!confirm(`Excluir a categoria "${item.name}"?`)) return;
      try {
        this.error = null;
        await CASH.deleteCategory(item.id);
        await this.loadCategories();
      } catch (error) {
        this.error = error.message || "Erro ao excluir categoria";
      }
    },
    async loadCategories() {
      try {
        this.loading = true;
        this.error = null;
        const params = {};
        if (this.companyId) params.companyId = this.companyId;
        const response = await CASH.getCategories(params);
        this.categories = response.categories || [];
      } catch (error) {
        this.error = error.message || "Erro ao carregar categorias";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadCategories();
  },
};
</script>
